import React, { useCallback } from 'react';
import { observer } from 'mobx-react-lite';
import { History } from 'history';
import {
  ICON_BUTTON_SIZE,
  ICON_BUTTON_TYPE,
  ICON_COLORS,
  MENU_OPTIONS,
  PROJECT_ICON_TYPE
} from '../../../global/constants';
import { ROUTES } from '../../../routes/urls';
import {
  AccountingViewState,
  CurrentProject,
  CurrentUser,
  CurrentWorkspace
} from '../../../interfaces/ProjectInterface';
import AddIconDropdown from '../../viewCard/addIconDropdown';
import { Dropdown, DropdownItem } from '../../dropdown';
import IconButton from '../../button/iconButton';
import ProjectIconAvatar from '../../projectIconAvatar';
import ResponsiveHeader from '../../responsiveHeader';
import TaskFilterCustomDropdown from '../../taskFilterDropdown';
import { ButtonBlock, Content, DropDiv, ProjectIconContainer, StarWithTitle, Title } from '../styles';
import AccountingProjectTabs from './AccountingProjectTabs';

interface AccountingHeaderProps {
  currentProject: CurrentProject;
  currentUser: CurrentUser;
  currentWorkspace: CurrentWorkspace;
  history: History;
  isTaskFilterOpen: boolean;
  params: { id: string };
  view: AccountingViewState;
  isMobile: boolean;
  mobileFilterContent: React.ReactNode;
  placement: 'mobile' | 'desktop';
  onArchive: () => void;
  onEditProject: () => void;
  renderTaskFilterIcon: () => JSX.Element;
}

const menuItems = [
  { key: MENU_OPTIONS.EDIT, label: 'Edit' },
  { key: MENU_OPTIONS.SETTING, label: 'Settings' },
  { key: MENU_OPTIONS.ARCHIVE, label: 'Archive' }
];

const AccountingHeader: React.FC<AccountingHeaderProps> = observer(
  ({
    currentProject,
    currentUser,
    currentWorkspace,
    history,
    isTaskFilterOpen,
    params,
    view,
    isMobile,
    mobileFilterContent,
    onArchive,
    onEditProject,
    placement,
    renderTaskFilterIcon
  }) => {
    const { isFile, isTask } = view;

    const onClickProjectSetting = useCallback(() => {
      history.push(ROUTES.PROJECT_SETTING.replace(':id', params.id || ''));
    }, [history, params.id]);

    const handleClick = useCallback(
      (key: number | string) => {
        if (Number(key) === MENU_OPTIONS.EDIT) onEditProject();
        if (Number(key) === MENU_OPTIONS.SETTING) onClickProjectSetting();
        if (Number(key) === MENU_OPTIONS.ARCHIVE) onArchive();
      },
      [onArchive, onClickProjectSetting, onEditProject]
    );

    if (placement === 'mobile') {
      return (
        <ResponsiveHeader
          applyDottedText={true}
          title={currentProject?.name || ''}
          isProjectDetail={true}
          noMargin={true}
          leftElement={
            (isTask || isFile) && (
              <TaskFilterCustomDropdown
                trigger={renderTaskFilterIcon}
                isTaskFilterOpen={isTask && isTaskFilterOpen}
                content={mobileFilterContent}
                isMinWidth={isFile ? 255 : 289}
              />
            )
          }
        />
      );
    }

    return (
      <Content>
        {!isMobile && (
          <ProjectIconContainer>
            {currentUser?.isAdmin || currentUser?.isOwner ? (
              <AddIconDropdown currentProject={currentProject!} />
            ) : (
              <ProjectIconAvatar
                size={26}
                type={currentProject?.icon?.type ?? PROJECT_ICON_TYPE.DEFAULT}
                reference={currentProject?.icon?.reference || ''}
                backgroundColor={currentProject?.icon?.color ? ICON_COLORS[currentProject.icon.color] : ''}
                projectName={currentProject?.name || ''}
                iconSize={16}
                isClickable={false}
              />
            )}
            <StarWithTitle>
              {!window.location.hostname.startsWith('embed') && <Title className='web'>{currentProject?.name}</Title>}
            </StarWithTitle>
          </ProjectIconContainer>
        )}
        <ButtonBlock>
          <AccountingProjectTabs
            currentProject={currentProject}
            currentWorkspace={currentWorkspace}
            history={history}
            params={params}
            view={view}
          />
          {(currentWorkspace?.isAdmin || currentWorkspace?.isOwner) && (
            <DropDiv>
              <Dropdown
                modalTitle='More'
                activeClassName='active-state'
                content={
                  <IconButton
                    iconName='three-dots-horizontal-icon'
                    size={ICON_BUTTON_SIZE.MEDIUM}
                    variant={ICON_BUTTON_TYPE.TERTIARY}
                    className='more-icon'
                  />
                }
                trigger='click'>
                {menuItems.map((item) => (
                  <DropdownItem
                    label={item.label}
                    key={item.key}
                    tone={item.label === 'Archive' ? 'critical' : 'primary'}
                    onClick={() => handleClick(item.key)}
                  />
                ))}
              </Dropdown>
            </DropDiv>
          )}
        </ButtonBlock>
      </Content>
    );
  }
);

export default AccountingHeader;
